import { useCallback } from 'react';

import {
  CANVAS_OBJECT_TYPE_PREFIX,
  SHAPE_ROTATION_DEGREES,
} from '~/core/constants';
import { DrawingAppliance, DrawingShape } from '~/core/models';
import { makeApplianceName } from '~/core/utils';
import { useDrawingShapeContext } from '~/legacy/easy-estimator-page/context';
import { useAppDispatch } from '~/redux/hooks';
import { setPreventRepricing } from '~/redux/slices/drawingShape';
import {
  clearSelectedDrawingTabs,
  updateSelectedDrawingObjectName,
} from '~/redux/slices/shape';

export const useActiveAppliance = () => {
  const dispatch = useAppDispatch();

  const {
    getters: { drawingShapes, activeDrawingApplianceId },
    handlers: { updateActiveDrawingAppliance, updateDrawingAppliances },
  } = useDrawingShapeContext();

  const activateAddedAppliance = useCallback(
    (drawingAppliance: DrawingAppliance) => {
      const {
        id,
        applianceModel: { consumerDesc, name },
      } = drawingAppliance;
      const applianceName = consumerDesc ?? name;

      updateDrawingAppliances((prev) => [...prev, drawingAppliance]);
      updateActiveDrawingAppliance(makeApplianceName(id));
      dispatch(updateSelectedDrawingObjectName(applianceName));
    },
    [dispatch, updateActiveDrawingAppliance, updateDrawingAppliances]
  );

  const rotateActiveDrawingAppliance = useCallback(
    (canvas: fabric.Canvas, clockwise: boolean) => {
      drawingShapes.forEach((shape: DrawingShape) => {
        const applianceIndex = shape.appliances.findIndex(
          (appliance: DrawingAppliance) =>
            `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${appliance.id}` ===
            activeDrawingApplianceId
        );

        if (applianceIndex > -1) {
          dispatch(setPreventRepricing(true));
          shape.appliances[applianceIndex].angle -=
            SHAPE_ROTATION_DEGREES * (clockwise ? -1 : 1);

          canvas
            .getActiveObject()
            ?.rotate(shape.appliances[applianceIndex].angle);
          dispatch(clearSelectedDrawingTabs());
        }
      });
    },
    [activeDrawingApplianceId, dispatch, drawingShapes]
  );

  const getAppliancesFromDrawingShapes = useCallback(
    (productId: number): DrawingAppliance[] =>
      drawingShapes.flatMap((shape: DrawingShape) =>
        shape.appliances.filter(
          (appliance: DrawingAppliance) =>
            appliance.applianceModel.productId === productId
        )
      ),
    [drawingShapes]
  );

  return {
    activateAddedAppliance,
    getAppliancesFromDrawingShapes,
    rotateActiveDrawingAppliance,
  };
};
